export type BootStep = "interface" | "orientation" | "display";
export interface BootSnapshot { settled: readonly BootStep[]; total: number; ready: boolean; failed?: readonly BootStep[] }

/** Real promise completion only: no timer-based percentage or artificial delay. */
export function createBootReadiness(tasks: Record<BootStep, Promise<unknown>>) {
  const listeners = new Set<() => void>();
  let snapshot: BootSnapshot = { settled: [], failed: [], total: Object.keys(tasks).length, ready: false };
  for (const [step, task] of Object.entries(tasks)) {
    const finish = (criticalFailure = false) => {
      const settled = criticalFailure ? snapshot.settled : [...snapshot.settled, step as BootStep];
      const failed = criticalFailure ? [...(snapshot.failed ?? []), step as BootStep] : snapshot.failed ?? [];
      snapshot = { settled, failed, total: snapshot.total, ready: !failed.length && settled.length === snapshot.total };
      listeners.forEach((listener) => listener());
    };
    // Optional layout/fonts have usable defaults. Failed code is not completion:
    // retain the localized, retryable startup screen instead of showing 100%.
    void task.then(() => finish(), () => finish(step === "interface"));
  }
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };
}

/** Typography/native preference may fall back to the already usable defaults. */
export async function optionalBootTask(task: Promise<unknown>, timeoutMs: number): Promise<void> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([task, new Promise<void>((resolve) => { timeout = setTimeout(resolve, timeoutMs); })]);
  } catch { /* Current viewport/system fonts remain usable. */ }
  finally { if (timeout !== undefined) clearTimeout(timeout); }
}
